import type { ReactNode } from "react";
import PaperItem from "@components/Paper/PaperItem";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { getMovementServiceDetailsById } from "@services/movementServicesApi";
import type {
  AssociatedMovementService,
  LinkedVesselCalls,
  PortService,
} from "@services/port-services/types";
import { useQuery } from "@tanstack/react-query";
import { paths } from "@utils/navigation";
import { t } from "i18next";
import { Link, useParams } from "react-router-dom";
import type { MovementServiceDetailsBackendResponse } from "../../MovementServices/preview/types";

const TEST_ID_BASE = "port-services-preview";
const wrapperStyleClass = "border-b border-[#F1F2F4] pb-3";
const transStyleClass = "font-normal text-[#646A77]";

type ShipToShipFieldsProps = {
  langBase: string;
  portServiceDetails: PortService;
};

type ShipToShipField = {
  key: string;
  labelKey: string;
  value?: string | number | ReactNode;
};

const formatValue = (value?: string | number) => value || "-";
const formatFieldValue = (value?: string | number | ReactNode) => {
  if (typeof value === "string" || typeof value === "number") {
    return formatValue(value);
  }

  return value ?? "-";
};

const getAssociatedMovementServices = (
  portServiceDetails: PortService,
): AssociatedMovementService[] => {
  if (portServiceDetails.associatedMovementServices?.length) {
    return portServiceDetails.associatedMovementServices;
  }

  if (portServiceDetails.associatedMovementService) {
    return [portServiceDetails.associatedMovementService];
  }

  if (portServiceDetails.associatedMovementServiceId) {
    return [
      {
        id: portServiceDetails.associatedMovementServiceId,
        requestNumber:
          portServiceDetails.associatedMovementServiceRequestNumber ??
          portServiceDetails.associatedMovementServiceNumber,
      },
    ];
  }

  return [];
};

const getAssociatedMovementServiceId = (service: AssociatedMovementService) =>
  service.id ?? service.movementServiceId;

const getAssociatedMovementServiceLabel = (service: AssociatedMovementService) =>
  service.requestNumber ??
  service.movementServiceNumber ??
  service.referenceNumber ??
  service.orderId ??
  getAssociatedMovementServiceId(service);

const getAssociatedMovementLabel = (
  movementId: string,
  movementDetails: MovementServiceDetailsBackendResponse,
) =>
  `${movementDetails.orderId ?? movementId} - ${formatValue(movementDetails.from)} - ${formatValue(
    movementDetails.to,
  )}`;

const getLinkedVesselAssociatedMovement = (
  linkedVessel: LinkedVesselCalls | undefined,
  movementDetails: MovementServiceDetailsBackendResponse | undefined,
): AssociatedMovementService | null => {
  if (!linkedVessel?.associatedMovementId || !movementDetails) {
    return null;
  }

  return {
    id: linkedVessel.associatedMovementId,
    requestNumber: getAssociatedMovementLabel(linkedVessel.associatedMovementId, movementDetails),
    vesselCallId: linkedVessel.vesselCallId,
  };
};

const deduplicateAssociatedMovementServices = (services: AssociatedMovementService[]) => {
  const seen = new Set<string>();

  return services.filter((service) => {
    const movementServiceId = getAssociatedMovementServiceId(service);

    if (!movementServiceId) {
      return true;
    }

    if (seen.has(movementServiceId)) {
      return false;
    }

    seen.add(movementServiceId);
    return true;
  });
};

const getFallbackAssociatedMovementServices = (
  portServiceDetails: PortService,
  currentVesselCallId: string | undefined,
) => {
  const services = getAssociatedMovementServices(portServiceDetails);

  if (!currentVesselCallId) {
    return services;
  }

  const currentVesselServices = services.filter(
    (service) => !service.vesselCallId || service.vesselCallId === currentVesselCallId,
  );

  return currentVesselServices.length ? currentVesselServices : services;
};

const ShipToShipFields: React.FC<ShipToShipFieldsProps> = ({ langBase, portServiceDetails }) => {
  const { id: currentVesselCallId } = useParams();
  const { isMarineDepartmentUser, isShippingAgentUser } = useAuthStore();
  const linkedVessels = portServiceDetails.linkedVesselCalls ?? [];
  const mainVessel = linkedVessels.find((vessel) => vessel.role === "MAIN");
  const secondaryVessel = linkedVessels.find((vessel) => vessel.role === "SECONDARY");
  const mainAssociatedMovementId = mainVessel?.associatedMovementId;
  const secondaryAssociatedMovementId = secondaryVessel?.associatedMovementId;
  const mainAssociatedMovementQuery = useQuery({
    queryKey: ["port-service-associated-movement", mainAssociatedMovementId],
    queryFn: () =>
      getMovementServiceDetailsById({
        service: "bff-vessel-call",
        id: mainAssociatedMovementId as string,
      }),
    enabled: Boolean(mainAssociatedMovementId),
  });
  const secondaryAssociatedMovementQuery = useQuery({
    queryKey: ["port-service-associated-movement", secondaryAssociatedMovementId],
    queryFn: () =>
      getMovementServiceDetailsById({
        service: "bff-vessel-call",
        id: secondaryAssociatedMovementId as string,
      }),
    enabled: Boolean(secondaryAssociatedMovementId),
  });
  const isViewingSecondaryVessel = secondaryVessel?.vesselCallId === currentVesselCallId;
  const selectedAssociatedMovement = isViewingSecondaryVessel
    ? getLinkedVesselAssociatedMovement(secondaryVessel, secondaryAssociatedMovementQuery.data)
    : getLinkedVesselAssociatedMovement(mainVessel, mainAssociatedMovementQuery.data);

  const associatedMovementServices = deduplicateAssociatedMovementServices(
    [
      selectedAssociatedMovement,
      ...(!selectedAssociatedMovement
        ? getFallbackAssociatedMovementServices(portServiceDetails, currentVesselCallId)
        : []),
    ].filter((service): service is AssociatedMovementService => Boolean(service)),
  );
  const associatedMovementServicesValue = associatedMovementServices.length ? (
    <div className="flex flex-col items-start gap-1">
      {associatedMovementServices.map((service, index) => {
        const movementServiceId = getAssociatedMovementServiceId(service);
        const label = getAssociatedMovementServiceLabel(service);
        const vesselCallId = service.vesselCallId ?? currentVesselCallId;
        const movementServicePath =
          movementServiceId && vesselCallId
            ? `/${paths.vesselCall.index}/${paths.vesselCall.vesselCallPreview}/${vesselCallId}/${paths.vesselCall.movementServices}/${movementServiceId}`
            : null;

        return movementServicePath && (isMarineDepartmentUser || isShippingAgentUser) ? (
          <Link
            key={`${movementServiceId}-${index}`}
            to={movementServicePath}
            className="font-medium text-[#0070C9] underline underline-offset-2"
            data-testid={`${TEST_ID_BASE}-associated-movement-service-link-${index}`}
          >
            {formatValue(label)}
          </Link>
        ) : (
          <span key={`${label}-${index}`}>{formatValue(label)}</span>
        );
      })}
    </div>
  ) : null;

  const fields: ShipToShipField[] = [
    {
      key: "associated-movement-services",
      labelKey: "associated-movement-services",
      value: associatedMovementServicesValue,
    },
  ];

  return (
    <>
      {fields.map(({ key, labelKey, value }) => (
        <PaperItem
          key={key}
          testId={`${TEST_ID_BASE}-${key}`}
          translate={t(`${langBase}.${labelKey}`)}
          textStyles="font-medium"
          tranStyle={transStyleClass}
          wrapperStyle={wrapperStyleClass}
          text={formatFieldValue(value)}
        />
      ))}
    </>
  );
};

export default ShipToShipFields;
